// ─────────────────────────────────────────────────────────────────────────────
// text_composition.ts — Passive handwriting tracker (+ legacy session mode)
//
// Default mode is **passive**: stroke commits accumulate into per-page
// "shadow groups" (one per spatial line), each group is recognised by HWR
// after a short settle, and the resulting text is stashed on the group as
// metadata. Strokes are never replaced or removed; the user's handwritten
// stylisation stays the headline. Renderers fetch groups via
// `getShadowGroups(pageId)` and draw the detected text faintly underneath
// each group on the astral layer.
//
// Tiny isolated strokes (dots, commas drawn alone, accent flicks) don't
// seed new groups — they only join an existing group within proximity.
// This avoids the recogniser firing on stray pen taps.
//
// Legacy interactive mode (hint ring → tap → block creation) is still
// reachable by constructing with `{ passive: false }`, but the canvas no
// longer drives that path. It's kept around for the future "convert to
// text block" gesture and for tests.
//
// Lifecycle (passive):
//   1. Stroke committed → feedStrokeWithPage → routed to a shadow group
//   2. Group's settle timer fires → HWR runs → group.text updated
//   3. Stroke erased → forgetStroke prunes the group; if any strokes remain
//      the group's settle timer is re-armed so the text refreshes.
//   4. getShadowGroups(pageId) returns the current map for rendering.
// ─────────────────────────────────────────────────────────────────────────────

import type { StrokePoint, BoundingBox } from '@shared/notebook_model.js';
import type { HandwritingRecognizer, HwrStroke } from './handwriting_recognizer.js';

// ── Public types ─────────────────────────────────────────────────────────────

export interface ComposeCallbacks {
  /** Create a new text block and return its blockId. */
  createBlock(pageId: string, bbox: BoundingBox, fontSize: number): string;
  /** Update existing text block content. */
  updateBlock(blockId: string, pageId: string, content: string): void;
  /** Erase consumed strokes from the ink layer. */
  eraseStrokes(pageId: string, strokeIds: string[]): void;
  /** Get current content of a text block. */
  getBlockContent(blockId: string): string | undefined;
  /** Measure text width at a given font size (for fit-to-content). */
  measureTextWidth(text: string, fontSize: number): number;
  /** Store a handwritten ink word on the text block. */
  storeInkWord(
    blockId: string,
    strokes: ComposePendingStrokes[],
    srcBBox: BoundingBox,
    dstBBox: { x: number; y: number; w: number; h: number },
  ): void;
}

export interface ComposePendingStrokes {
  id: string;
  points: StrokePoint[];
}

// ── Pending hint — waiting for user tap to confirm ───────────────────────────

export interface ComposeHint {
  /** Recognized text. */
  text: string;
  /** Stroke bounding box (used for positioning the ring & deriving fontSize). */
  strokeBBox: BoundingBox;
  /** IDs of strokes that produced this hint. */
  strokeIds: string[];
  /** Full stroke data (for ink word storage). */
  strokes: ComposePendingStrokes[];
  /** Page the strokes live on. */
  pageId: string;
  /** Font size derived from these strokes' height. */
  fontSize: number;
  /** Position for the hint tap ring (left of the stroke bbox). */
  ringX: number;
  ringY: number;
  /** Birth time for expiry. */
  birth: number;
}

// ── Session state ────────────────────────────────────────────────────────────

export interface ComposeSession {
  /** Text block being composed into. */
  blockId: string;
  pageId: string;
  /** Character index where next word will be inserted. */
  cursor: number;
  /** Font size derived from the first word's stroke height. */
  fontSize: number;
  /** Block bounding box. */
  bbox: BoundingBox;
  /** Timestamp of last recognized word — idle timeout ends session. */
  lastActivity: number;
}

// ── Constants ────────────────────────────────────────────────────────────────

/** Delay after last stroke before triggering recognition (ms). */
const SETTLE_MS = 1200;
/** Idle timeout after last recognition before session auto-closes (ms). */
const IDLE_TIMEOUT_MS = 8000;
/** Minimum stroke height (px) to derive font size — below this use default. */
const MIN_STROKE_HEIGHT = 6;
/** Default font size if strokes are too small to measure. */
const DEFAULT_FONT_SIZE = 16;
/** Padding around text block bbox. */
const BLOCK_PAD = 6;
/** How far left of the stroke bbox the ring appears. */
const RING_OFFSET_X = 28;

// ── Passive shadow-group constants ───────────────────────────────────────────

/** Strokes whose max bounding-box dimension is below this won't seed a new
 *  group on their own — only attach to a nearby existing group. Picks out
 *  pure dots, commas, and tiny accent flicks. */
const TINY_STROKE_DIM = 4;
/** Vertical proximity threshold (px) for joining a group: a new stroke joins
 *  if its centre y is within (group.height / 2) + this slack of the group's
 *  centre. Roughly "same line of writing". */
const LINE_GROUP_SLACK_Y = 8;
/** Horizontal proximity threshold (px) — strokes farther right than the
 *  group's right edge by more than this start a new word group instead. */
const LINE_GROUP_SLACK_X = 80;

// ── Passive shadow group ─────────────────────────────────────────────────────

/** A spatial cluster of strokes treated as one "line" for recognition.
 *  Strokes are NOT consumed — the group is purely meta information layered
 *  alongside the user's ink. Mutations (new stroke joining, stroke erased)
 *  re-arm a settle timer that re-recognises and updates `text` in place. */
export interface ShadowGroup {
  id: string;
  pageId: string;
  /** Stroke ids in this group, in feed order. */
  strokeIds: string[];
  /** Stroke point arrays, keyed by id — needed for re-recognition. */
  strokes: Map<string, ComposePendingStrokes>;
  /** Most recently recognised text for this group ('' until first reco). */
  text: string;
  /** Union bbox of all member strokes. */
  bbox: BoundingBox;
  /** Last successful recognition wall-clock time. */
  lastReco: number;
}

// ── Main class ───────────────────────────────────────────────────────────────

export class TextComposition {
  private _reco: HandwritingRecognizer;
  private _cb: ComposeCallbacks;

  /** Active composition session (at most one). */
  private _session: ComposeSession | null = null;

  /** Strokes accumulated since last recognition. */
  private _pendingStrokes: ComposePendingStrokes[] = [];

  /** Settle timer — fires recognition after writing pauses. */
  private _settleTimer = 0;

  /** Idle timer — ends session after inactivity. */
  private _idleTimer = 0;

  /** Currently running recognition (to avoid overlapping calls). */
  private _recognizing = false;

  /** Pending hint waiting for user tap confirmation. */
  private _hint: ComposeHint | null = null;

  /** Track last pageId from feedStroke calls. */
  private _lastPageId: string | null = null;

  // ── Passive (shadow-group) state ────────────────────────────────────────
  /** Whether feedStrokeWithPage runs the passive shadow-group pipeline (true)
   *  or the legacy interactive hint-ring pipeline (false). Default: passive. */
  private _passive = true;
  /** pageId → groupId → ShadowGroup. */
  private _shadowGroups = new Map<string, Map<string, ShadowGroup>>();
  /** Per-group settle timers (browser timeout handles), keyed by groupId. */
  private _groupTimers = new Map<string, number>();
  /** Monotonic group-id counter. */
  private _nextGroupId = 1;

  constructor(recognizer: HandwritingRecognizer, callbacks: ComposeCallbacks) {
    this._reco = recognizer;
    this._cb = callbacks;
  }

  // ── Mode toggle ─────────────────────────────────────────────────────────

  /** Switch between passive (shadow-group) and legacy (hint-ring) pipelines.
   *  The canvas wires this on construction; tests / future gestures may
   *  flip it ad-hoc. Toggling clears any in-flight session/hint to keep
   *  the pipelines from leaking state into each other. */
  setPassive(passive: boolean): void {
    if (this._passive === passive) return;
    this._passive = passive;
    this._hint = null;
    this._session = null;
    this._pendingStrokes = [];
    this._clearTimers();
  }

  get passive(): boolean { return this._passive; }

  // ── Public API ───────────────────────────────────────────────────────────

  /** Whether a composition session is active. */
  get active(): boolean { return this._session !== null; }

  /** Current session info (for rendering cursor). */
  get session(): Readonly<ComposeSession> | null { return this._session; }

  /** Pending hint awaiting user confirmation (for rendering ring). */
  get hint(): Readonly<ComposeHint> | null { return this._hint; }

  /**
   * Feed a committed stroke into the composition pipeline.
   *
   * Passive mode (default): the stroke is bucketed into the nearest shadow
   * group on its page (or starts a new one if no neighbour is close
   * enough), and that group's recognition timer is re-armed. The user's
   * ink is left untouched.
   *
   * Legacy mode: stroke joins a flat pending-buffer, and after the settle
   * delay the whole buffer is recognised and surfaced as a tap-to-confirm
   * hint that, on confirmation, replaces the strokes with a text block.
   */
  feedStrokeWithPage(stroke: ComposePendingStrokes, pageId: string): void {
    this._lastPageId = pageId;
    if (this._passive) {
      this._feedShadow(stroke, pageId);
      return;
    }
    this._pendingStrokes.push(stroke);
    this._resetSettleTimer();
    if (this._session) this._resetIdleTimer();
  }

  /** A stroke was erased on the page — drop it from any shadow group it
   *  was a member of. If the group still has strokes, re-arm its settle
   *  timer so the text re-recognises with the new stroke set; if it's
   *  empty, delete the group. Idempotent for unknown stroke ids. */
  forgetStroke(strokeId: string, pageId: string): void {
    const groups = this._shadowGroups.get(pageId);
    if (!groups) return;
    for (const [gid, g] of groups) {
      if (!g.strokes.has(strokeId)) continue;
      g.strokes.delete(strokeId);
      g.strokeIds = g.strokeIds.filter(id => id !== strokeId);
      if (g.strokes.size === 0) {
        groups.delete(gid);
        const t = this._groupTimers.get(gid);
        if (t) { clearTimeout(t); this._groupTimers.delete(gid); }
      } else {
        this._recomputeGroupBBox(g);
        this._scheduleGroupReco(g);
      }
      return;
    }
  }

  /** Per-page list of shadow groups for renderers. Returns a snapshot —
   *  callers must not mutate the returned arrays. Empty array if the page
   *  has no detected text yet. */
  getShadowGroups(pageId: string): ShadowGroup[] {
    const m = this._shadowGroups.get(pageId);
    if (!m) return [];
    return Array.from(m.values());
  }

  /**
   * User tapped the hint ring — commit the recognized text.
   * Returns true if a hint was consumed.
   */
  confirmHint(): boolean {
    const h = this._hint;
    if (!h) return false;
    this._hint = null;

    if (this._session) {
      this._insertAtCursor(h.text, h.strokeIds, h.strokes, h.strokeBBox, h.pageId);
    } else {
      this._startNewSession(h.text, h.strokeBBox, h.strokeIds, h.strokes, h.pageId, h.fontSize);
    }
    return true;
  }

  /** Dismiss the hint without applying it. */
  dismissHint(): void {
    this._hint = null;
  }

  /**
   * Handle a tap on existing text — repositions the cursor.
   * Returns true if the tap was consumed (hit the active block).
   */
  tapOnBlock(blockId: string, charIndex: number): boolean {
    if (!this._session || this._session.blockId !== blockId) return false;
    this._session.cursor = charIndex;
    this._resetIdleTimer();
    return true;
  }

  /** End the session (tap elsewhere, page switch, tool change, etc.). */
  end(): void {
    this._clearTimers();
    this._hint = null;
    this._session = null;
    this._pendingStrokes = [];
  }

  /** Start a new session targeting an existing text block (e.g. tap on text). */
  startOnExistingBlock(
    blockId: string,
    pageId: string,
    cursor: number,
    fontSize: number,
    bbox: BoundingBox,
  ): void {
    this.end();
    this._session = { blockId, pageId, cursor, fontSize, bbox, lastActivity: Date.now() };
    this._resetIdleTimer();
  }

  // ── Passive (shadow-group) pipeline ───────────────────────────────────

  /** Route a freshly-committed stroke to the shadow-group store. Tiny
   *  isolated strokes don't seed groups (they only join existing ones);
   *  this stops dots / commas / accent flicks from triggering recognition
   *  on their own. */
  private _feedShadow(stroke: ComposePendingStrokes, pageId: string): void {
    const sBbox = strokesBBox([stroke]);
    if (sBbox.width <= 0 && sBbox.height <= 0) return;
    const tiny = Math.max(sBbox.width, sBbox.height) < TINY_STROKE_DIM;

    let pageMap = this._shadowGroups.get(pageId);
    if (!pageMap) { pageMap = new Map(); this._shadowGroups.set(pageId, pageMap); }

    const target = this._findGroupFor(sBbox, pageMap);
    if (!target) {
      if (tiny) return;  // no neighbour to attach to, and stroke is too tiny to seed
      const id = 'sg' + (this._nextGroupId++);
      const fresh: ShadowGroup = {
        id,
        pageId,
        strokeIds: [stroke.id],
        strokes: new Map([[stroke.id, stroke]]),
        text: '',
        bbox: { ...sBbox },
        lastReco: 0,
      };
      pageMap.set(id, fresh);
      this._scheduleGroupReco(fresh);
      return;
    }

    target.strokes.set(stroke.id, stroke);
    target.strokeIds.push(stroke.id);
    target.bbox = unionBBox(target.bbox, sBbox);
    this._scheduleGroupReco(target);
  }

  /** Pick the best existing group on the page for a new stroke, or null
   *  if no group is close enough. Heuristic: same line of writing
   *  (vertical centres within slack), and not separated horizontally by a
   *  whole-paragraph gap. Among candidates, prefer the one with smallest
   *  centre-to-centre distance. */
  private _findGroupFor(
    sBbox: BoundingBox,
    pageMap: Map<string, ShadowGroup>,
  ): ShadowGroup | null {
    const sCx = sBbox.x + sBbox.width / 2;
    const sCy = sBbox.y + sBbox.height / 2;
    let best: ShadowGroup | null = null;
    let bestDist = Infinity;
    for (const g of pageMap.values()) {
      const gCy = g.bbox.y + g.bbox.height / 2;
      const halfH = g.bbox.height / 2 + LINE_GROUP_SLACK_Y;
      if (Math.abs(sCy - gCy) > halfH) continue;
      // Horizontal: stroke should overlap the group or sit within slack.
      const left  = g.bbox.x - LINE_GROUP_SLACK_X;
      const right = g.bbox.x + g.bbox.width + LINE_GROUP_SLACK_X;
      const sLeft  = sBbox.x;
      const sRight = sBbox.x + sBbox.width;
      const overlapsX = sRight >= left && sLeft <= right;
      if (!overlapsX) continue;
      const gCx = g.bbox.x + g.bbox.width / 2;
      const d = Math.hypot(sCx - gCx, sCy - gCy);
      if (d < bestDist) { bestDist = d; best = g; }
    }
    return best;
  }

  /** Re-arm the per-group settle timer so quick successive strokes land
   *  in the same recognition pass. Idle groups recognise after SETTLE_MS. */
  private _scheduleGroupReco(g: ShadowGroup): void {
    const existing = this._groupTimers.get(g.id);
    if (existing) clearTimeout(existing);
    const t = window.setTimeout(() => {
      this._groupTimers.delete(g.id);
      void this._recognizeGroup(g);
    }, SETTLE_MS);
    this._groupTimers.set(g.id, t);
  }

  /** Run HWR over the group's current strokes and write the result back
   *  onto the group. Failure leaves the previous text intact (it's better
   *  to show stale-but-plausible than to flicker the meta layer). */
  private async _recognizeGroup(g: ShadowGroup): Promise<void> {
    if (g.strokes.size === 0) return;
    const hwrStrokes: HwrStroke[] = g.strokeIds
      .map(id => g.strokes.get(id))
      .filter((s): s is ComposePendingStrokes => !!s)
      .map(s => ({ id: s.id, points: s.points }));
    if (hwrStrokes.length === 0) return;
    try {
      const result = await this._reco.recognize(hwrStrokes);
      const text = (result.text ?? '').trim();
      if (text) {
        g.text = text;
        g.lastReco = Date.now();
      }
    } catch {
      // Recognition failed — keep the previous text on this group.
    }
  }

  /** Recompute a group's bbox after a stroke is removed. */
  private _recomputeGroupBBox(g: ShadowGroup): void {
    const list = g.strokeIds
      .map(id => g.strokes.get(id))
      .filter((s): s is ComposePendingStrokes => !!s);
    g.bbox = strokesBBox(list);
  }

  // ── Timers ─────────────────────────────────────────────────────────────

  private _resetSettleTimer(): void {
    if (this._settleTimer) clearTimeout(this._settleTimer);
    this._settleTimer = window.setTimeout(() => {
      this._settleTimer = 0;
      this._triggerRecognition();
    }, SETTLE_MS);
  }

  private _resetIdleTimer(): void {
    if (this._idleTimer) clearTimeout(this._idleTimer);
    this._idleTimer = window.setTimeout(() => {
      this._idleTimer = 0;
      this.end();
    }, IDLE_TIMEOUT_MS);
  }

  private _clearTimers(): void {
    if (this._settleTimer) { clearTimeout(this._settleTimer); this._settleTimer = 0; }
    if (this._idleTimer) { clearTimeout(this._idleTimer); this._idleTimer = 0; }
  }

  // ── Recognition flow ───────────────────────────────────────────────────

  private async _triggerRecognition(): Promise<void> {
    if (this._recognizing || this._pendingStrokes.length === 0) return;
    this._recognizing = true;

    // Snapshot and clear pending strokes
    const strokes = this._pendingStrokes.slice();
    this._pendingStrokes = [];
    const strokeIds = strokes.map(s => s.id);
    const pageId = this._lastPageId ?? '';

    // Derive bounding box from strokes
    const bbox = strokesBBox(strokes);

    try {
      const hwrStrokes: HwrStroke[] = strokes.map(s => ({ id: s.id, points: s.points }));
      const result = await this._reco.recognize(hwrStrokes);

      if (!result.text) { this._recognizing = false; return; }
      const text = result.text.trim();
      if (!text) { this._recognizing = false; return; }

      // Derive font size from stroke height
      let fontSize = this._session?.fontSize ?? 0;
      if (!fontSize) {
        fontSize = Math.max(MIN_STROKE_HEIGHT, bbox.height);
        if (fontSize < 10) fontSize = DEFAULT_FONT_SIZE;
        if (fontSize > 48) fontSize = 48;
        fontSize = Math.round(fontSize);
      }

      // Place the hint ring to the LEFT of the stroke bbox, vertically centered
      const ringX = bbox.x - RING_OFFSET_X;
      const ringY = bbox.y + bbox.height / 2;

      this._hint = {
        text,
        strokeBBox: bbox,
        strokeIds,
        strokes,
        pageId,
        fontSize,
        ringX,
        ringY,
        birth: performance.now(),
      };
    } catch {
      // Recognition failed — leave strokes on page as ink
    } finally {
      this._recognizing = false;
    }
  }

  // ── Commit actions ─────────────────────────────────────────────────────

  private _startNewSession(
    text: string,
    strokeBBox: BoundingBox,
    strokeIds: string[],
    strokes: ComposePendingStrokes[],
    pageId: string,
    fontSize: number,
  ): void {
    // Measure text width for a snug block
    const textWidth = this._cb.measureTextWidth(text + ' ', fontSize);

    const blockBBox: BoundingBox = {
      x: strokeBBox.x - BLOCK_PAD,
      y: strokeBBox.y - BLOCK_PAD,
      width: textWidth + BLOCK_PAD * 2,
      height: fontSize + BLOCK_PAD * 2,
    };

    const blockId = this._cb.createBlock(pageId, blockBBox, fontSize);

    const content = text + ' ';
    this._cb.updateBlock(blockId, pageId, content);

    // Store ink word at its original position (block-local)
    const srcBBox = { x: strokeBBox.x, y: strokeBBox.y, w: strokeBBox.width, h: strokeBBox.height };
    const dstBBox = { ...srcBBox }; // first word stays at original position
    this._cb.storeInkWord(blockId, strokes, strokeBBox, dstBBox);

    // Remove strokes from ink layer
    this._cb.eraseStrokes(pageId, strokeIds);

    this._session = {
      blockId,
      pageId,
      cursor: content.length,
      fontSize,
      bbox: blockBBox,
      lastActivity: Date.now(),
    };
    this._resetIdleTimer();
  }

  private _insertAtCursor(
    text: string,
    strokeIds: string[],
    strokes: ComposePendingStrokes[],
    strokeBBox: BoundingBox,
    pageId: string,
  ): void {
    const s = this._session!;
    const existing = this._cb.getBlockContent(s.blockId) ?? '';

    const insert = text + ' ';
    const newContent = existing.slice(0, s.cursor) + insert + existing.slice(s.cursor);

    // Compute destination bbox: scale to session fontSize, place after last ink word
    const scale = s.fontSize / Math.max(strokeBBox.height, 1);
    const dstW = strokeBBox.width * scale;

    // Cursor position from OCR text
    const ocrCursorX = s.bbox.x + BLOCK_PAD + this._cb.measureTextWidth(
      existing.slice(0, s.cursor), s.fontSize,
    );

    const dstBBox = {
      x: ocrCursorX,
      y: s.bbox.y + BLOCK_PAD,
      w: dstW,
      h: s.fontSize,
    };

    // Store the ink word with its transform destination
    this._cb.storeInkWord(s.blockId, strokes, strokeBBox, dstBBox);

    s.cursor += insert.length;
    s.lastActivity = Date.now();

    // Update content first, then let the callback resize the bbox
    this._cb.updateBlock(s.blockId, s.pageId, newContent);

    // Sync session bbox width with actual ink + text bounds
    const textWidth = this._cb.measureTextWidth(newContent, s.fontSize);
    const inkRight = dstBBox.x + dstBBox.w - s.bbox.x + BLOCK_PAD;
    s.bbox = {
      ...s.bbox,
      width: Math.max(textWidth + BLOCK_PAD * 2, inkRight + BLOCK_PAD),
    };

    this._cb.eraseStrokes(pageId, strokeIds);
    this._resetIdleTimer();
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function strokesBBox(strokes: ComposePendingStrokes[]): BoundingBox {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of strokes) {
    for (const p of s.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  if (minX === Infinity) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Bounding-box union: smallest rect containing both. */
function unionBBox(a: BoundingBox, b: BoundingBox): BoundingBox {
  if (a.width === 0 && a.height === 0) return { ...b };
  if (b.width === 0 && b.height === 0) return { ...a };
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const r = Math.max(a.x + a.width, b.x + b.width);
  const btm = Math.max(a.y + a.height, b.y + b.height);
  return { x, y, width: r - x, height: btm - y };
}
